import {
  FixedBPointsReward,
  FixedGemsReward,
  isRandomReward,
  Match2Reward,
} from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { animateJumpingPoints } from './jumpingPoints';
import { RacesButtonAnimated } from '../minigame/match2-timestep/races/components/RacesButtonAnimated';
import { TeamCompetitionButton } from '../minigame/match2-timestep/team/components/TeamCompetitionButton';
import { animateFlyingPoints } from './flyingPoints';
import {
  getApplication,
  getFooter,
  getHeader,
  getSceneManager,
} from 'src/lib/utils';
import { getMatch2FrenzyEventConfig } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import StateObserver, { now, user } from 'src/StateObserver';
import { getFrenzyObjectiveIcon } from '../minigame/match2-timestep/match2.icons';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { match2TournamentRuleset } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import { Match2TournamentButtonAnimated } from '../minigame/match2-timestep/tournament/Match2TournamentButtonAnimated';
import { getTournamentIconImage } from '../minigame/match2-timestep/tournament/match2-tournament.utils';
import { getActiveRace } from 'src/replicant/match2/races/races.getters';
import { getRaceTheme } from '../minigame/match2-timestep/races/races.utils';
import { View } from '@play-co/timestep-core/ui';
import type { Match2ProgressDailyChallengeArgs } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.actions';
import { getDailyChallenge } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.getters';
import type { Match2DailyChallengeId } from 'src/replicant/match2/dailychallenge/match2-dailychallenge.ruleset';
import { Match2DailyChallengeButtonAnimated } from '../minigame/match2-timestep/dailychallenge/Match2DailyChallengeButtonAnimated';
import { getMatch2RewardAsset } from '../minigame/match2-timestep/rewards/match2-rewards.utils';
import { SuperLollipopButton } from '../superLollipop/components/SuperLollipopButton';
import { WeeklyLeaderboardButton } from '../weeklyLeaderboard/components/WeeklyLeaderboardButton';
import { TreasureExpressHomeButton } from '../treasureExpress/components/TreasureExpressHomeButton';
import { BuddiesButton } from '../buddies/components/BuddiesButton';
import { setCompensation } from '../inventory/inventory.redux';
import { CARDS_REWARD_ICON_PATH } from '../cards/cards.assets';

/**
 * Retrieve one of the home buttons by tag
 * @param tag
 * @returns
 */
export function getHomeButton<T>(tag: string): T | undefined {
  const button = getSceneManager()?.currentScene?.getIcon(tag);
  return button as T | undefined;
}

/**
 * Retrieve all home buttons
 * @param tag
 * @returns
 */
export function getAllHomeButtons() {
  return (
    getSceneManager()?.currentScene?.getHomeButtons()?.getAllButtons() ?? []
  );
}

/**
 * Animate a list of puzzle rewards, plus tournament points if puzzle stars are provided.
 * The list will be split in 3 batches of animations, to be played in squential order.
 * 1st batch: bPoints and gems
 * 2nd batch: tournament points (from stars)
 * 3rd batch: remaining features rewards
 * @param args
 */
export async function animatePuzzleRewards(args: {
  rewards: Match2Reward[];
  dailyChallengeProgress?: Match2ProgressDailyChallengeArgs[];
  stars?: number;
  shorterAnimation?: boolean;
  boostersAndPowerUps?: boolean;
  superLollipopProgress?: boolean;
  weeklyLeaderboardProgress?: boolean;
  puzzleAttempts?: number;
  onBeforeRewards?: () => Promise<void>;
  compensate?: boolean;
  frenzyRewards?: Match2Reward[];
}) {
  // Block the screen to avoid any interaction while the rewards are being animated
  const blocker = new View({
    superview: getApplication().getRootView(),
    infinite: true,
    canHandleEvents: true,
    zIndex: 10000, // Under popups.
  });

  // Callback to async function before rewards, if we want to do something while screen is blocked
  await args.onBeforeRewards?.();

  if (args.shorterAnimation) {
    // If playing short animation, call instant update on all home buttons
    // So they can sync up with the user state
    const allHomeButtons = getAllHomeButtons();
    for (const button of allHomeButtons) {
      button.instantUpdate?.();
    }
  }

  const firstBatch = args.rewards.filter(
    (reward) => reward.type === 'bPoints' || reward.type === 'gems',
  );

  const secondBatch = args.rewards.filter(
    (reward) =>
      reward.type === 'feature' && reward.subType === 'frenzy_objective',
  );

  const thirdBatch = args.rewards.filter(
    (reward) =>
      !(firstBatch as Match2Reward[]).includes(reward) &&
      !(secondBatch as Match2Reward[]).includes(reward),
  );

  const forthBatch = args.rewards.filter(
    (reward) => reward.type === 'boosters' || reward.type === 'powerups',
  );

  // Animate the first batch one by one
  for (const reward of firstBatch) {
    await animatePuzzleReward({
      reward,
      compensate: args.compensate,
    });
  }

  // Animate the second batch one by one
  for (const reward of secondBatch) {
    await animatePuzzleReward({ reward, frenzyRewards: args.frenzyRewards });
  }

  // ANimate only the main rewards and skip jumping points in shorter version
  if (args.shorterAnimation) {
    blocker.removeFromSuperview();
    return;
  }

  // Animate buddies progress
  if (args.puzzleAttempts !== undefined) {
    await animateBuddiesProgress(args.puzzleAttempts);
  }

  // Daily challenge points are not given within regular rewards, so we animate it separately
  if (args.dailyChallengeProgress) {
    await animateDailyChallengePoints(args.dailyChallengeProgress);
  }

  // Tournament points is not given within regular rewards, so we animate it separately
  if (args.stars) {
    await animateTournamentPoints(args.stars);
  }

  // Super lollipop streak is a special case, we animate it separately
  if (args.superLollipopProgress) {
    await animateSuperLollipopProgress();
  }

  if (args.weeklyLeaderboardProgress && args.stars) {
    await animateWeeklyLeaderboardsProgress(args.stars);
  }

  // Animate progress related to puzzle attempts
  if (args.puzzleAttempts !== undefined) {
    await animateTreasureExpressProgress(args.puzzleAttempts);
  }

  // Animate the third batch one by one
  for (const reward of thirdBatch) {
    await animatePuzzleReward({ reward });
  }

  // Animate boosters and powerups
  if (args.boostersAndPowerUps && forthBatch.length > 0) {
    await animateBoostersAndPowerUps({ rewards: forthBatch });
  }

  // Remove the blocker
  blocker.removeFromSuperview();
}

/**
 * Play a single reward animation based on the reward type
 * @param args
 * @returns
 */
async function animatePuzzleReward(args: {
  reward: Match2Reward;
  compensate?: boolean;
  frenzyRewards?: Match2Reward[];
}) {
  const { reward, compensate } = args;

  if (reward.type === 'bPoints') {
    const fixedReward = reward as FixedBPointsReward;
    if (!fixedReward.amount) return;
    await animateCoins({
      count: fixedReward.amount,
      onLand: compensate
        ? (remaining) =>
            StateObserver.dispatch(setCompensation({ bPoints: remaining }))
        : undefined,
    });
    if (compensate) {
      StateObserver.dispatch(setCompensation({ bPoints: 0 }));
    }
  }

  if (reward.type === 'gems') {
    const fixedReward = reward as FixedGemsReward;
    if (!fixedReward.amount) return;
    await animateGems({
      count: fixedReward.amount,
      onLand: compensate
        ? (remaining) =>
            StateObserver.dispatch(setCompensation({ gems: remaining }))
        : undefined,
    });
    if (compensate) {
      StateObserver.dispatch(setCompensation({ gems: 0 }));
    }
  }

  if (reward.type === 'card') {
    const button = getHomeButton<View>('cards');
    if (!button || !button.style.visible) return;

    await animateJumpingPoints({
      button,
      count: 1,
      iconImage: CARDS_REWARD_ICON_PATH,
      iconSize: 70,
    });
  }

  if (reward.type === 'feature') {
    if (reward.subType === 'frenzy_objective') {
      await animateFrenzyObjective({
        count: reward.amount,
        rewards: args.frenzyRewards ?? [],
      });
    }

    if (reward.subType === 'race_points') {
      await animateRacePoints({ count: reward.amount });
    }

    if (reward.subType === 'team_chest_event') {
      await animateTeamEventPoints({
        count: reward.amount,
        type: reward.subType,
      });
    }
  }
}

/**
 * Play coins animation
 * @param args
 * @returns
 */
async function animateCoins(args: {
  count: number;
  onLand?: (remaining: number) => void;
}) {
  const root = getApplication().getRootView();
  const target = getHeader().getCoinIconPos(root);
  if (!target) return;
  await animateFlyingPoints({
    count: args.count,
    target,
    iconImage: 'assets/ui/hud/icons/hud_coin.png',
    onLand: args.onLand,
  });
}

/**
 * Play gems animation
 * @param args
 * @returns
 */
async function animateGems(args: {
  count: number;
  onLand?: (remaining: number) => void;
}) {
  const root = getApplication().getRootView();
  const target = getHeader().getGemsIconPos(root);
  if (!target) return;
  await animateFlyingPoints({
    count: args.count,
    target,
    iconImage: 'assets/ui/hud/icons/hud_gem.png',
    onLand: args.onLand,
  });
}

/**
 * Play frenzy objective animation
 * @param args
 * @returns
 */
export async function animateFrenzyObjective(args: {
  count: number;
  rewards?: Match2Reward[];
}) {
  if (!getHeader().isMatch2FrenzyBarVisible()) return;

  const config = getMatch2FrenzyEventConfig(user(), now());
  if (!config) return;

  const image = getFrenzyObjectiveIcon(config.theme).getCached();
  if (!image) return;

  const root = getApplication().getRootView();
  const target = getHeader().getMatch2FrenzyBarObjectivePos(root);
  if (!target) return;

  await animateFlyingPoints({
    count: args.count,
    target,
    iconImage: image,
  });

  await getHeader().match2FrenzyBar.animateState(args.rewards ?? []);
}

/**
 * Play tournament points animation
 * @param stars
 * @returns
 */
export async function animateTournamentPoints(stars: number) {
  // Skip if the home button is not available
  const button = getHomeButton<Match2TournamentButtonAnimated>('tournament');
  if (!button || !button.style.visible) return;

  // Find out if there is an ongoing event
  const ongoingEvent = match2TournamentGetters.getOngoingEvent(user(), now());
  if (!ongoingEvent) return;

  // Star tournament is simple, just progress with the stars
  if (ongoingEvent.type === 'star') {
    await animateJumpingPoints({
      button,
      count: stars,
      iconImage: 'assets/ui/match2/tournament/star_tournament_icon.png',
    });
    return;
  }

  // Streak tournament score is stars * multiplier - we have to find out the
  // actual score based on the previous multiplier, since it has been updated already
  const { tournamentMultiplier } = match2TournamentRuleset;
  const multiplierIndex = Math.min(
    Math.max(ongoingEvent.multiplier - 1, 0),
    tournamentMultiplier.length - 1,
  );
  const multiplier = tournamentMultiplier[multiplierIndex];
  const score = stars * multiplier;
  await animateJumpingPoints({
    button,
    count: score,
    iconImage: getTournamentIconImage('streak'),
  });
}

/**
 * Play team event or team competition points animation
 * @param args
 * @returns
 */
async function animateTeamEventPoints(args: { count: number; type: string }) {
  const button = getHomeButton<TeamCompetitionButton>('teamEvent');
  if (!button || !button.style.visible) return;

  const image =
    args.type === 'team_chest_event'
      ? 'assets/ui/match2/team/chest/team_chest_icon.png'
      : 'assets/ui/match2/team/competition/icon_teamCompetition_sm.png';

  await animateJumpingPoints({
    button,
    count: args.count,
    iconImage: image,
  });
}

/**
 * Play race poinst animation, only for timed races - target races don't have points animation
 * @param args
 * @returns
 */
async function animateRacePoints(args: { count: number }) {
  const button = getHomeButton<RacesButtonAnimated>('race');
  if (!button || !button.style.visible) return;

  const activeRace = getActiveRace(now());
  if (!activeRace) return;

  const { config } = activeRace;
  if (config.type === 'target') return;

  const iconImage = getRaceTheme(config).icon.image as string;
  if (!iconImage) return;

  await animateJumpingPoints({
    button,
    count: args.count,
    iconImage,
  });
}

/**
 * Animate the daily challenge points. It is a bit different from the other rewards: it receives
 * a list of progresses made for the daily challenge (comes from the match2 post game sequence)
 * but we only animate the one that matches the current challenge.
 * @param progress
 * @returns
 */
async function animateDailyChallengePoints(
  progress: Match2ProgressDailyChallengeArgs[],
) {
  const button =
    getHomeButton<Match2DailyChallengeButtonAnimated>('dailyChallenge');
  if (!button || !button.style.visible) return;

  const dailyChallenge = user().match2.dailyChallenge;
  if (!dailyChallenge) return;

  const currentChallenge = getDailyChallenge(dailyChallenge.day);
  if (!currentChallenge) return;

  // From given items, find the one that matches the current challenge
  const selected = progress.find((p) => {
    const challengeGoal = currentChallenge.goal as {
      type: string;
      subType?: string;
    };
    const itemGoal = p.goal as { type: string; subType?: string };
    const matchType = challengeGoal.type === itemGoal.type;
    const matchSubType = challengeGoal.subType === itemGoal.subType;
    return matchType && matchSubType;
  });

  // If no match, skip
  if (!selected) return;

  // Map the daily challenge id to the icon image
  const iconMap: Record<Match2DailyChallengeId, string> = {
    monday: 'assets/ui/match2/goals/icon.bomb.png',
    tuesday: 'assets/ui/match2/daily_challenges/tuesday/icon_tuesday.png',
    wednesday: 'assets/ui/match2/goals/icon.cube.all.png',
    thursday: 'assets/ui/match2/daily_challenges/thursday/icon_thursday.png',
    friday: 'assets/ui/match2/daily_challenges/friday/button/8.png',
    saturday: 'assets/ui/match2/goals/icon.rocket.vertical.png',
    sunday: 'assets/ui/match2/daily_challenges/sunday/icon_sunday.png',
  };

  // Find the icon image for the current challenge, if not found, skip
  const iconImage = iconMap[currentChallenge.id];
  if (!iconImage) return;

  // Animate the points
  await animateJumpingPoints({
    button,
    count: selected.amount,
    iconImage,
    iconSize: 60,
  });
}

/**
 * Animate boosters and powerups
 */
async function animateBoostersAndPowerUps(args: { rewards: Match2Reward[] }) {
  const { rewards } = args;

  const root = getApplication().getRootView();
  const target = getFooter().getMatch2PlayIconPos(root);
  if (!target) return Promise.resolve();

  const animations = rewards.map(async (reward) => {
    if (isRandomReward(reward)) return Promise.resolve();
    const amount = 'amount' in reward ? reward.amount : 1;
    const image = await getMatch2RewardAsset(reward);
    if (!image) return Promise.resolve();

    return animateFlyingPoints({
      count: amount,
      target,
      iconImage: image,
      hideScore: true,
    });
  });

  return Promise.all(animations);
}

/**
 * Play super lollipop progress animation
 */
async function animateSuperLollipopProgress() {
  const button = getHomeButton<SuperLollipopButton>('superLollipop');
  if (!button || !button.style.visible) return;
  await button.playProgressAnimation();
}

/**
 * Play weekly leaderboards progress animation
 */
async function animateWeeklyLeaderboardsProgress(stars: number) {
  const button = getHomeButton<WeeklyLeaderboardButton>('weeklyLeaderboard');
  if (!button || !button.style.visible) return;
  await button.playProgressAnimation(stars);
}

/**
 * Play weekly leaderboards progress animation
 */
async function animateTreasureExpressProgress(puzzleAttempts: number) {
  const button = getHomeButton<TreasureExpressHomeButton>('treasureExpress');
  if (!button || !button.style.visible) return;
  await button.playProgressAnimation(puzzleAttempts);
}

/**
 * Play Baking Buddies progress animation
 */
async function animateBuddiesProgress(puzzleAttempts: number) {
  const button = getHomeButton<BuddiesButton>('buddies');
  if (!button || !button.style.visible) return;
  await button.playProgressAnimation(puzzleAttempts);
}
